import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import useEmblaCarousel from 'embla-carousel-react';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { Video } from "../../../../api/interfaces/Video";
import { PinnacleChip } from "../../../../components/PinnacleChip";
import VideoListItem from "../VideoListItem/VideoListItem";
import { PinnacleDialog } from "../../../../components/PinnacleDialog";
import VideoGalleryItem from "../VideoGalleryItem/VideoGalleryItem";
import { useMatch, useNavigate } from "react-router-dom";

interface SubjectListPartialProps {
    key?: string;
    subjectId?: string;
    title?: string;
    videos?: Video[];
    isAccountTrial: boolean;
    isControlsEnabled?: boolean;
    isShowAllEnabled?: boolean;
    isSliderEnabled?: boolean;
    onDialogOpenCheckTrial?: () => Promise<void>;
    isRecentlyWatch?: boolean;
}

const SubjectListPartial: React.FC<SubjectListPartialProps> = ({
    subjectId,
    title,
    videos,
    isAccountTrial,
    isControlsEnabled,
    isShowAllEnabled,
    isSliderEnabled,
    onDialogOpenCheckTrial,
    isRecentlyWatch
}) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    // const { subjectId: paramSubjectId } = useParams<{ subjectId?: string }>();

    const galleryMatch = useMatch("/videos/gallery/:subjectId");
    const isShowAllOpen = !!galleryMatch && galleryMatch.params.subjectId === subjectId;

    // const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    // const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    // const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    // const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', containScroll: 'trimSnaps' });

    const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
    const scrollNext = () => emblaApi && emblaApi.scrollNext();

    const handleShowAll = () => {
        navigate(`/videos/gallery/${subjectId}`);
    };

    const handleClose = () => {
        navigate("/");
    };

    return (
        <>
            <Box sx={{ mb: 4 }}>
                {isMobile && (
                    <Typography variant="h5" sx={{ pb: 2 }}>
                        {title}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mb: 2,
                        gap: 1,
                    }}
                >
                    {/* Arrows group */}
                    {isControlsEnabled && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <IconButton
                                sx={{
                                    border: '2px solid #90B0D2',
                                    borderRadius: '50%',
                                    color: '#90B0D2',
                                    width: 40,
                                    height: 40,
                                    '&:hover': {
                                        borderColor: 'lightgray',
                                        backgroundColor: 'transparent',
                                    },
                                }}
                                onClick={scrollPrev}
                            >
                                <ArrowBackIosOutlinedIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                                sx={{
                                    border: '2px solid #90B0D2',
                                    borderRadius: '50%',
                                    color: '#90B0D2',
                                    width: 40,
                                    height: 40,
                                    '&:hover': {
                                        borderColor: 'lightgray',
                                        backgroundColor: 'transparent',
                                    },
                                }}
                                onClick={scrollNext}
                            >
                                <ArrowForwardIosOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}

                    {/* Title + Show All group */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            gap: 1,
                            minWidth: 0,
                        }}
                    >
                        {!isMobile && (
                            <Typography
                                className="ellipsis"
                                variant="h5"
                                sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: { xs: '150px', sm: '400px', md: '500px' },
                                }}
                            >
                                {title}
                            </Typography>
                        )}

                        {isShowAllEnabled && (
                            <PinnacleChip
                                label="Show All"
                                size="medium"
                                variant="outlined"
                                onClick={() => handleShowAll()}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(var(--rgb-secondary)) !important',
                                        cursor: 'pointer',
                                        color: '#000'
                                    },
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        width: '100%',
                        flexWrap: 'wrap',
                        minWidth: 0,
                    }}
                    ref={isSliderEnabled ? emblaRef : null}
                >

                    <Box
                        sx={{
                            display: 'flex',
                        }}
                    >
                        {Array.isArray(videos) && videos.length > 0 && (
                            videos.map((video, index) => (
                                <Box
                                    key={video._id}
                                    sx={{
                                        display: 'flex',
                                        flex: {
                                            xs: '0 0 calc(50% - 8px)',
                                            sm: '0 0 calc(33.33% - 8px)',
                                            md: '0 0 calc(25% - 8px)',
                                            lg: '0 0 calc(20% - 8px)',
                                        },
                                        mr: 2,
                                    }}
                                >
                                    <VideoListItem
                                        keyIndex={video._id}
                                        thumbnail={video.thumbnail}
                                        title={video.title}
                                        duration={video.duration}
                                        isAccountTrial={isAccountTrial}
                                        video={video}
                                        isRecentlyWatch={isRecentlyWatch}
                                    />
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
            {/* SHOW ALL DIALOG */}
            < PinnacleDialog
                isOpen={isShowAllOpen}
                setIsOpen={handleClose}
                maxWidth={false}
                headerTitle={title}
                renderShowAllVideosPerSubject={true}
            >
                <VideoGalleryItem
                    subjectId={subjectId}
                    title={title}
                    isAccountTrial={isAccountTrial}
                />
            </PinnacleDialog >
        </>
    );
};

export default SubjectListPartial;